import ApiService from '@/core/services/ApiService';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import { Module, Action, Mutation, VuexModule } from 'vuex-module-decorators';

export interface PackageValidationRuleObject {
  id: string;
  name: string;
  ruleType: string;
  classCategoryId?: string;
  packageCountMin?: number;
  packageCountMax?: number;
  horseTypeId?: string;
  lessonTypeId?: string;
  numberOfCancellation: number;
  numberOfDays: number;
  numberOfDaysError?: string;
  priority: number;
  isActive: boolean;
  classCategory?: any;
  horseType?: any;
  lessonType?: any;
}

export interface PackageValidationRuleListInfo {
  page: number;
  take: number;
  itemCount: number;
  pageCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  packageValidationRules: Array<PackageValidationRuleObject>;
  errors: unknown;
}

export interface PackageValidationRuleFilterInfo {
  name?: string;
  ruleType?: string;
  isActive?: boolean;
  classCategoryId?: string;
  horseTypeId?: string;
  lessonTypeId?: string;
}

@Module
export default class PackageValidationRuleModule
  extends VuexModule
  implements PackageValidationRuleListInfo
{
  packageValidationRules = [] as Array<PackageValidationRuleObject>;
  allPackageValidationRules = [] as Array<PackageValidationRuleObject>;
  packageValidationRuleTypes = [] as Array<any>;
  packageValidationRuleFilter = {} as PackageValidationRuleFilterInfo;
  page = 1;
  take = 10;
  itemCount = 0;
  pageCount = 1;
  hasPreviousPage = false;
  hasNextPage = true;
  errors = {};

  /**
   * Get package validation rule list
   * @returns Array<PackageValidationRuleObject>
   */
  get packageValidationRuleList(): Array<PackageValidationRuleObject> {
    return this.packageValidationRules;
  }

  get allPackageValidationRuleList(): Array<PackageValidationRuleObject> {
    return this.allPackageValidationRules;
  }

  get packageValidationRuleTypesList(): Array<any> {
    return this.packageValidationRuleTypes;
  }

  /**
   * Get total items
   * @returns number
   */
  get packageValidationRuleListCount(): number {
    return this.itemCount;
  }

  @Mutation
  [Mutations.SET_PACKAGE_VALIDATION_RULES](data) {
    this.packageValidationRules = data.data;
    this.page = data.meta.page;
    this.take = data.meta.take;
    this.itemCount = data.meta.itemCount;
    this.pageCount = data.meta.pageCount;
    this.hasPreviousPage = data.meta.hasPreviousPage;
    this.hasNextPage = data.meta.hasNextPage;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_ALL_PACKAGE_VALIDATION_RULES](data) {
    this.allPackageValidationRules = data.data;
  }

  @Mutation
  [Mutations.SET_PACKAGE_VALIDATION_RULE_CURRENT_PAGE](page) {
    this.page = page;
  }

  @Mutation
  [Mutations.RESET_PACKAGE_VALIDATION_RULE_LIST]() {
    this.packageValidationRules = [] as Array<PackageValidationRuleObject>;
    this.page = 1;
    this.take = 10;
    this.itemCount = 0;
    this.pageCount = 1;
    this.hasPreviousPage = false;
    this.hasNextPage = true;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_PACKAGE_VALIDATION_RULE_FILTER](obj) {
    this.packageValidationRuleFilter = {
      ...this.packageValidationRuleFilter,
      ...obj,
    };
  }

  @Mutation
  [Mutations.SET_PACKAGE_VALIDATION_RULE_TYPES](data) {
    this.packageValidationRuleTypes = data;
  }

  @Action
  [Actions.GET_PACKAGE_VALIDATION_RULES]() {
    const params = {
      page: this.page,
      name: this.packageValidationRuleFilter.name,
      ruleType: this.packageValidationRuleFilter.ruleType,
      isActive: this.packageValidationRuleFilter.isActive,
      classCategoryId: this.packageValidationRuleFilter.classCategoryId,
      horseTypeId: this.packageValidationRuleFilter.horseTypeId,
      lessonTypeId: this.packageValidationRuleFilter.lessonTypeId,
    };

    return ApiService.query('settings/package-validation-rule', { params })
      .then(({ data }) => {
        this.context.commit(Mutations.SET_PACKAGE_VALIDATION_RULES, data);
        return data;
      })
      .catch(({ response }) => {
        console.error('Error fetching package validation rules:', response);
        if (response && response.data && response.data.errors) {
          this.context.commit(Mutations.SET_ERROR, response.data.errors);
        }
      });
  }

  @Action
  [Actions.GET_ALL_PACKAGE_VALIDATION_RULES]() {
    return ApiService.get('settings/package-validation-rule/all')
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ALL_PACKAGE_VALIDATION_RULES, data);
        return data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, response.data.errors);
      });
  }

  @Action
  [Actions.GET_PACKAGE_VALIDATION_RULE_TYPES]() {
    return ApiService.get('settings/package-validation-rule/ruleType')
      .then(({ data }) => {
        console.log('Rule types API response:', data);
        console.log('Rule types type:', typeof data);
        console.log('Rule types is array:', Array.isArray(data));
        // The API wraps the array in a data property
        const ruleTypes = data.data || data;
        console.log('Extracted rule types:', ruleTypes);
        this.context.commit(
          Mutations.SET_PACKAGE_VALIDATION_RULE_TYPES,
          ruleTypes
        );
        return ruleTypes;
      })
      .catch((error) => {
        console.error('Error fetching rule types:', error);
        console.error('Error response:', error.response);
        if (
          error.response &&
          error.response.data &&
          error.response.data.errors
        ) {
          this.context.commit(Mutations.SET_ERROR, error.response.data.errors);
        }
      });
  }

  @Action
  [Actions.GET_PACKAGE_VALIDATION_RULE](id) {
    return ApiService.get('settings/package-validation-rule', id)
      .then(({ data }) => {
        return data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, response.data.errors);
      });
  }

  @Action
  [Actions.CREATE_PACKAGE_VALIDATION_RULE](payload) {
    return ApiService.post('settings/package-validation-rule', payload)
      .then(({ data }) => {
        return data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, response.data.errors);
      });
  }

  @Action
  [Actions.UPDATE_PACKAGE_VALIDATION_RULE](payload) {
    return ApiService.update(
      'settings/package-validation-rule',
      payload.id,
      payload
    )
      .then(({ data }) => {
        return data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, response.data.errors);
      });
  }

  @Action
  [Actions.DELETE_PACKAGE_VALIDATION_RULE](id) {
    return ApiService.delete('settings/package-validation-rule', id)
      .then(({ data }) => {
        return data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, response.data.errors);
      });
  }

  @Action
  [Actions.UPDATE_PACKAGE_VALIDATION_RULE_CURRENT_PAGE](page) {
    this.context.commit(
      Mutations.SET_PACKAGE_VALIDATION_RULE_CURRENT_PAGE,
      page
    );
  }

  @Action
  [Actions.UPDATE_PACKAGE_VALIDATION_RULE_FILTER](obj) {
    this.context.commit(Mutations.SET_PACKAGE_VALIDATION_RULE_FILTER, obj);
  }

  @Action
  [Actions.RESET_PACKAGE_VALIDATION_RULE_STORE]() {
    this.context.commit(Mutations.RESET_PACKAGE_VALIDATION_RULE_LIST);
    this.context.commit(Mutations.SET_PACKAGE_VALIDATION_RULE_FILTER, {});
  }
}
